import React from 'react';
import PropTypes from 'prop-types';
import { LETTER_URL } from '../../constants';
import NeedHelp from '../NeedHelp';

const ConfirmationDenied = ({
  confirmationDate,
  printPage,
  formData,
}) => {
  // Helper function to render a field if it exists
  const renderField = (label, value) => {
    if (!value || (typeof value === 'string' && !value.trim())) {
      return null;
    }
    return (
      <div className="vads-u-margin-bottom--2">
        <h4 className="vads-u-margin-bottom--0p5 vads-u-font-weight--bold">
          {label}
        </h4>
        <p className="vads-u-margin--0">{value}</p>
      </div>
    );
  };

  // Helper to format full name
  const formatFullName = nameObj => {
    if (!nameObj) return '';
    const parts = [nameObj.first, nameObj.middle, nameObj.last, nameObj.suffix].filter(
      Boolean,
    );
    return parts.join(' ');
  };

  // Helper to format address
  const formatAddress = addressObj => {
    if (!addressObj) return '';
    const parts = [
      addressObj.street,
      addressObj.street2,
      addressObj.city,
      addressObj.state,
      addressObj.postalCode,
      addressObj.country,
    ].filter(Boolean);
    return parts.join(', ');
  };

  // Helper to format date
  const formatDate = dateString => {
    if (!dateString) return '';
    const date = new Date(`${dateString}T00:00:00`);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Helper to format yes/no
  const formatYesNo = value => {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    return '';
  };

  // Helper to format phone number
  const formatPhone = phoneObj => {
    if (!phoneObj) return '';
    if (typeof phoneObj === 'string') return phoneObj;
    return phoneObj.contact || phoneObj.phone || '';
  };

  // Helper to format salary from camelCase
  const formatSalary = salaryValue => {
    if (!salaryValue) return '';
    // Convert camelCase to readable format
    return salaryValue
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  return (
    <div className="vt2-confirmation-page vt2-confirmation-page_denied">
      <va-alert status="info">
        <h2 slot="headline">You're not eligible for this benefit</h2>
        <p>
          Unfortunately, based on the information you provided and Department of
          Defense records, we have determined you are not eligible for the VET
          TEC 2.0 Program at this time.
        </p>
        <p>
          Your denial letter, which explains why you are ineligible, is now
          available. A physical copy will also be mailed to your mailing
          address.
        </p>
        <va-link
          download
          href={LETTER_URL}
          filetype="PDF"
          text="Download your letter"
          class="vads-u-margin-top--2"
        />
      </va-alert>

      <div className="vads-u-margin-top--3">
        {confirmationDate && (
          <p>
            <strong>Date received:</strong> {confirmationDate}
          </p>
        )}

        <va-accordion open-single>
          <va-accordion-item header="Information you submitted on this form">
            {/* Applicant Information */}
            {formData?.applicantFullName &&
              renderField('Full name', formatFullName(formData.applicantFullName))}
            {renderField(
              'Date of birth',
              formData?.dateOfBirth && formatDate(formData.dateOfBirth),
            )}
            {renderField('Social Security number', formData?.claimantSsn)}

            {/* Contact Information */}
            {renderField('Email address', formData?.contactInfo?.emailAddress)}
            {renderField(
              'Mobile phone',
              formatPhone(formData?.contactInfo?.mobilePhone),
            )}
            {renderField('Home phone', formatPhone(formData?.contactInfo?.homePhone))}
            {renderField('Contact method', formData?.contactMethod)}

            {/* Address Information */}
            {formData?.mailingAddress &&
              renderField('Mailing address', formatAddress(formData.mailingAddress))}

            {/* Military Information */}
            {formData?.dutyRequirementMet !== undefined &&
              renderField(
                'Met duty requirement',
                formatYesNo(formData.dutyRequirementMet),
              )}
            {renderField(
              'Date released from active duty',
              formData?.dateReleasedFromActiveDuty &&
                formatDate(formData.dateReleasedFromActiveDuty),
            )}
            {formData?.activeDutyDuringHitechVets !== undefined &&
              renderField(
                'On active duty during HI-TECH VETS',
                formatYesNo(formData.activeDutyDuringHitechVets),
              )}

            {/* Employment Information */}
            {formData?.isEmployed !== undefined &&
              renderField('Currently employed', formatYesNo(formData.isEmployed))}
            {formData?.isInTechnologyIndustry !== undefined &&
              renderField(
                'Working in technology industry',
                formatYesNo(formData.isInTechnologyIndustry),
              )}
            {renderField('Technology area of focus', formData?.technologyAreaOfFocus)}
            {renderField('Current salary', formatSalary(formData?.currentSalary))}
            {renderField('Highest level of education', formData?.highestLevelOfEducation)}

            {/* Training Providers */}
            {formData?.trainingProviders &&
              formData.trainingProviders.length > 0 && (
                <div className="vads-u-margin-bottom--2">
                  <h4 className="vads-u-margin-bottom--0p5 vads-u-font-weight--bold">
                    Training providers
                  </h4>
                  {formData.trainingProviders.map((provider, index) => (
                    <div key={index} className="vads-u-margin-bottom--1">
                      <p className="vads-u-margin--0">
                        <strong>{provider.providerName}</strong>
                      </p>
                      {provider.providerAddress && (
                        <p className="vads-u-margin--0">
                          {formatAddress(provider.providerAddress)}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            {renderField(
              'Planned training start date',
              formData?.plannedStartDate && formatDate(formData.plannedStartDate),
            )}

            {/* Direct Deposit Information */}
            {formData?.bankAccount && (
              <>
                {renderField('Account type', formData.bankAccount.accountType)}
                {renderField('Routing number', formData.bankAccount.routingNumber)}
                {renderField(
                  'Account number',
                  formData.bankAccount.accountNumber &&
                    `****${formData.bankAccount.accountNumber.slice(-4)}`,
                )}
              </>
            )}
          </va-accordion-item>
        </va-accordion>

        <h3>Print this confirmation page</h3>
        <p>
          If you'd like to keep a copy of the information on this page, you can
          print it now. You won't be able to access this page later. Please note
          that an email with this information will be automatically sent to your
          email provided on the application.
        </p>
        <va-button
          text="Print this page for your records"
          onClick={printPage}
        />
      </div>

      <h2>What to expect next</h2>
      <ul>
        <li>
          We will review your eligibility for other VA education benefit
          programs.
        </li>
        <li>
          You will be notified if you are eligible for other VA education
          benefits.
        </li>
        <li>There is no further action required by you at this time.</li>
      </ul>

      <h3>If you believe this decision is incorrect</h3>
      <p>
        If you need help with your application or have questions about
        enrollment or eligibility, please contact{' '}
        <va-link href="/contact-us/ask-va" text="Ask VA" />.
      </p>

      <h2>How to contact us if you have questions</h2>
      <p>
        If you have questions about this form or need help, you can submit a
        request with <va-link href="/contact-us/ask-va" text="Ask VA" />.
      </p>

      <va-link-action
        href="/"
        text="Go back to VA.gov homepage"
        type="primary"
        class="vads-u-margin-top--2"
      />

      <div className="help-footer-box vads-u-margin-top--4">
        <h2 className="help-heading">Need help?</h2>
        <NeedHelp />
      </div>
    </div>
  );
};

ConfirmationDenied.propTypes = {
  confirmationDate: PropTypes.string,
  formData: PropTypes.object,
  printPage: PropTypes.func,
};

export default ConfirmationDenied;
